import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const estimatingEstimatesConfigurationValueSchema = z.strictObject({
    maximumEstimates: z.union([
        z.literal(1_000), z.literal(5_000), z.literal(10_000), z.literal(25_000),
    ]),
    maximumLinesPerEstimate: z.number().int().min(1).max(500),
    defaultCurrencyCode: z.string().regex(/^[A-Z]{3}$/),
    defaultTaxBasisPoints: z.number().int().min(0).max(10_000),
    defaultDiscountBasisPoints: z.number().int().min(0).max(10_000),
    defaultExpirationDays: z.number().int().min(1).max(365),
    maximumExpirationDays: z.number().int().min(1).max(365),
    estimateNumberPrefix: z.string().regex(/^[A-Z][A-Z0-9-]{0,7}$/),
    estimateNumberStart: z.number().int().min(1).max(999_999),
    allowStaffAcceptance: z.boolean(),
}).superRefine((value, context) => {
    if (value.defaultExpirationDays > value.maximumExpirationDays) {
        context.addIssue({
            code: 'custom',
            path: ['defaultExpirationDays'],
            message: 'Default expiration may not exceed the maximum.',
        });
    }
});
export const estimatingEstimatesConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'estimating.estimates.configuration',
        moduleId: 'estimating',
        title: 'Estimate behavior',
        helpText: 'Controls exact estimate limits, money defaults, expiration, serialized numbering, and staff acceptance.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            maximumEstimates: 10_000,
            maximumLinesPerEstimate: 250,
            defaultCurrencyCode: 'USD',
            defaultTaxBasisPoints: 0,
            defaultDiscountBasisPoints: 0,
            defaultExpirationDays: 30,
            maximumExpirationDays: 90,
            estimateNumberPrefix: 'EST',
            estimateNumberStart: 1,
            allowStaffAcceptance: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: estimatingEstimatesConfigurationValueSchema,
});
export const estimatingEstimatesDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showProjectRevision: z.boolean(),
    showTaxBreakdown: z.boolean(),
    showOptionalTotal: z.boolean(),
    showIntegrityHashes: z.boolean(),
    projectPanelEnabled: z.boolean(),
});
export const estimatingEstimatesDisplaySetting = definePlatformSetting({
    definition: {
        key: 'estimating.estimates.display',
        moduleId: 'estimating',
        title: 'Estimate display',
        helpText: 'Controls list density, card sizing, totals, integrity details, and the Project-hosted summary.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            pageSize: 50,
            density: 'comfortable',
            cardSize: 'standard',
            showProjectRevision: false,
            showTaxBreakdown: true,
            showOptionalTotal: true,
            showIntegrityHashes: false,
            projectPanelEnabled: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: estimatingEstimatesDisplayValueSchema,
});
const label = z.string().trim().min(1).max(160);
const estimatingEstimatesCopyVersionTwoAdditions = {
    detailsAction: 'View details',
    closeDetailsAction: 'Close details',
    printPreviewAction: 'Print preview',
    printAction: 'Print estimate',
    projectFilterLabel: 'Project',
    allProjectsLabel: 'All Projects',
    resetFiltersAction: 'Reset filters',
    revisionLabel: 'Revision',
    createdAtLabel: 'Created',
    updatedAtLabel: 'Last updated',
    lineItemsLabel: 'Estimate lines',
    acceptanceLabel: 'Acceptance',
    noAcceptanceLabel: 'No acceptance recorded',
    expiryNotSetLabel: 'No expiration scheduled',
    expiryDueLabel: 'Expiration is due',
    expiryScheduledLabel: 'Scheduled expiration',
    expiryClosedLabel: 'Lifecycle completed',
    integrityHashLabel: 'Content integrity hash',
};
export const estimatingEstimatesCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    projectPanelTitle: label,
    projectPanelEmpty: z.string().trim().min(1).max(300),
    createAction: label,
    reviseAction: label,
    releaseAction: label,
    viewAction: label,
    acceptAction: label,
    declineAction: label,
    expireAction: label,
    voidAction: label,
    detailsAction: label,
    closeDetailsAction: label,
    printPreviewAction: label,
    printAction: label,
    searchLabel: label,
    statusFilterLabel: label,
    tierFilterLabel: label,
    projectFilterLabel: label,
    allStatusesLabel: label,
    allTiersLabel: label,
    allProjectsLabel: label,
    resetFiltersAction: label,
    estimateNumberLabel: label,
    projectLabel: label,
    titleLabel: label,
    tierLabel: label,
    statusLabel: label,
    customerMessageLabel: label,
    internalNotesLabel: label,
    taxLabel: label,
    discountLabel: label,
    lineNameLabel: label,
    lineGroupLabel: label,
    quantityLabel: label,
    unitLabel: label,
    unitPriceLabel: label,
    taxableLabel: label,
    optionalLabel: label,
    addLineAction: label,
    removeLineAction: label,
    reasonLabel: label,
    expirationLabel: label,
    acceptedByLabel: label,
    acceptanceNoteLabel: label,
    subtotalLabel: label,
    optionalTotalLabel: label,
    discountTotalLabel: label,
    taxTotalLabel: label,
    totalLabel: label,
    revisionLabel: label,
    createdAtLabel: label,
    updatedAtLabel: label,
    lineItemsLabel: label,
    acceptanceLabel: label,
    noAcceptanceLabel: label,
    expiryNotSetLabel: label,
    expiryDueLabel: label,
    expiryScheduledLabel: label,
    expiryClosedLabel: label,
    integrityHashLabel: label,
    loadingLabel: label,
    savingLabel: label,
    savedLabel: label,
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    permissionDenied: z.string().trim().min(1).max(240),
    emptyTitle: label,
    emptyDescription: z.string().trim().min(1).max(300),
    statusLabels: z.strictObject({
        draft: label,
        released: label,
        viewed: label,
        accepted: label,
        declined: label,
        expired: label,
        voided: label,
    }),
    tierLabels: z.strictObject({ standard: label, premium: label, complete: label }),
});
export const estimatingEstimatesCopySetting = definePlatformSetting({
    definition: {
        key: 'estimating.copy.estimates',
        moduleId: 'estimating',
        title: 'Estimate copy',
        helpText: 'Controls tenant-editable estimate, tier, lifecycle, total, filter, and Project-panel labels.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            navigationLabel: 'Estimating',
            title: 'Estimates',
            description: 'Build exact retained estimates, release proposals, and preserve every revision and acceptance fact.',
            projectPanelTitle: 'Project estimates',
            projectPanelEmpty: 'No estimates are linked to this Project.',
            createAction: 'Create estimate',
            reviseAction: 'Save estimate revision',
            releaseAction: 'Release estimate',
            viewAction: 'Record first view',
            acceptAction: 'Record acceptance',
            declineAction: 'Decline estimate',
            expireAction: 'Expire estimate',
            voidAction: 'Void estimate',
            ...estimatingEstimatesCopyVersionTwoAdditions,
            searchLabel: 'Search estimates',
            statusFilterLabel: 'Status',
            tierFilterLabel: 'Proposal tier',
            allStatusesLabel: 'All statuses',
            allTiersLabel: 'All tiers',
            estimateNumberLabel: 'Estimate number',
            projectLabel: 'Project',
            titleLabel: 'Estimate title',
            tierLabel: 'Proposal tier',
            statusLabel: 'Status',
            customerMessageLabel: 'Customer message',
            internalNotesLabel: 'Internal notes',
            taxLabel: 'Tax rate',
            discountLabel: 'Discount rate',
            lineNameLabel: 'Line item',
            lineGroupLabel: 'Line group',
            quantityLabel: 'Quantity',
            unitLabel: 'Unit',
            unitPriceLabel: 'Unit price',
            taxableLabel: 'Taxable',
            optionalLabel: 'Optional',
            addLineAction: 'Add line',
            removeLineAction: 'Remove line',
            reasonLabel: 'Change reason',
            expirationLabel: 'Expires at',
            acceptedByLabel: 'Accepted by',
            acceptanceNoteLabel: 'Acceptance note',
            subtotalLabel: 'Subtotal',
            optionalTotalLabel: 'Optional work',
            discountTotalLabel: 'Discount',
            taxTotalLabel: 'Tax',
            totalLabel: 'Total',
            loadingLabel: 'Loading estimates…',
            savingLabel: 'Saving estimate…',
            savedLabel: 'Saved',
            loadError: 'Estimates could not be loaded.',
            mutationError: 'The estimate change could not be saved. Refresh and try again.',
            permissionDenied: 'You do not have access to change this estimate.',
            emptyTitle: 'No estimates found',
            emptyDescription: 'Create a retained draft for a Project to begin estimating.',
            statusLabels: {
                draft: 'Draft', released: 'Released', viewed: 'Viewed', accepted: 'Accepted',
                declined: 'Declined', expired: 'Expired', voided: 'Voided',
            },
            tierLabels: { standard: 'Standard', premium: 'Premium', complete: 'Complete' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: estimatingEstimatesCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...estimatingEstimatesCopyVersionTwoAdditions,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        }],
});
export const estimatingProposalsDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    maximumVisibleLines: z.number().int().min(3).max(20),
    defaultSelectedTier: z.enum(['standard', 'premium', 'complete']),
    showProjectRevision: z.boolean(),
    showTaxBreakdown: z.boolean(),
    showOptionalTotal: z.boolean(),
    showIntegrityHash: z.boolean(),
});
export const estimatingProposalsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'estimating.proposals.display', moduleId: 'estimating',
        title: 'Proposal presentation display',
        helpText: 'Controls proposal preview sizing, default tier, visible scope, totals, and integrity details.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            pageSize: 50, cardSize: 'wide', maximumVisibleLines: 8,
            defaultSelectedTier: 'complete', showProjectRevision: false,
            showTaxBreakdown: true, showOptionalTotal: true, showIntegrityHash: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: estimatingProposalsDisplayValueSchema,
});
export const estimatingProposalsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: label,
    description: z.string().trim().min(1).max(300),
    packagesTitle: label,
    previewTitle: label,
    packageLabel: label,
    projectLabel: label,
    generatedAtLabel: label,
    statusLabel: label,
    openLabel: label,
    lockedLabel: label,
    unavailableLabel: label,
    selectedLabel: label,
    selectableLabel: label,
    draftLabel: label,
    supersededLabel: label,
    expiredLabel: label,
    declinedLabel: label,
    voidedLabel: label,
    lockedOptionLabel: label,
    customerMessageLabel: label,
    includedWorkLabel: label,
    optionalLabel: label,
    subtotalLabel: label,
    optionalTotalLabel: label,
    discountLabel: label,
    taxLabel: label,
    totalLabel: label,
    expiresAtLabel: label,
    noExpirationLabel: label,
    printAction: label,
    loadingLabel: label,
    loadError: z.string().trim().min(1).max(240),
    emptyLabel: z.string().trim().min(1).max(300),
    tierLabels: z.strictObject({ standard: label, premium: label, complete: label }),
});
export const estimatingProposalsCopySetting = definePlatformSetting({
    definition: {
        key: 'estimating.copy.proposals', moduleId: 'estimating',
        title: 'Proposal presentation copy',
        helpText: 'Controls tenant-editable customer-safe proposal preview, tier, availability, and total labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Proposals', title: 'Proposal presentations',
            description: 'Review the exact customer-safe Standard, Premium, and Complete content retained by Estimating.',
            packagesTitle: 'Retained proposal packages', previewTitle: 'Customer-safe preview',
            packageLabel: 'Proposal package', projectLabel: 'Project', generatedAtLabel: 'Generated', statusLabel: 'Selection status',
            openLabel: 'Open for selection', lockedLabel: 'Selection locked', unavailableLabel: 'Not ready for selection',
            selectedLabel: 'Selected', selectableLabel: 'Available', draftLabel: 'Release this option before presenting',
            supersededLabel: 'Regenerate this package after estimate changes', expiredLabel: 'Expired',
            declinedLabel: 'Declined', voidedLabel: 'Voided', lockedOptionLabel: 'Locked after another option was accepted',
            customerMessageLabel: 'Message', includedWorkLabel: 'Included work', optionalLabel: 'Optional',
            subtotalLabel: 'Subtotal', optionalTotalLabel: 'Optional work', discountLabel: 'Discount',
            taxLabel: 'Tax', totalLabel: 'Total', expiresAtLabel: 'Expires', noExpirationLabel: 'Not released',
            printAction: 'Print customer-safe preview', loadingLabel: 'Loading proposal presentations…',
            loadError: 'Proposal presentations could not be loaded.',
            emptyLabel: 'No retained proposal packages match this view.',
            tierLabels: { standard: 'Standard', premium: 'Premium', complete: 'Complete' },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: estimatingProposalsCopyValueSchema,
});
export const estimatingCatalogConfigurationValueSchema = z.strictObject({
    maximumPricebookItems: z.union([
        z.literal(1_000), z.literal(10_000), z.literal(50_000), z.literal(100_000),
    ]),
    maximumEstimateTemplates: z.number().int().min(1).max(5_000),
    maximumPricebookImportRows: z.number().int().min(1).max(25_000),
    allowImportCategoryCreation: z.boolean(),
    importConflictStrategy: z.literal('source-key-then-sku'),
    allowPackageDraftReplacement: z.boolean(),
});
export const estimatingCatalogConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'estimating.catalog.configuration',
        moduleId: 'estimating',
        title: 'Estimating catalog behavior',
        helpText: 'Controls retained pricebook/template limits, deterministic CSV imports, and package draft replacement.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            maximumPricebookItems: 50_000,
            maximumEstimateTemplates: 1_000,
            maximumPricebookImportRows: 10_000,
            allowImportCategoryCreation: true,
            importConflictStrategy: 'source-key-then-sku',
            allowPackageDraftReplacement: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: estimatingCatalogConfigurationValueSchema,
});
export const estimatingCatalogDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showInactivePricebookItems: z.boolean(),
    showExternalProvenance: z.boolean(),
    showArchivedTemplates: z.boolean(),
});
export const estimatingCatalogDisplaySetting = definePlatformSetting({
    definition: {
        key: 'estimating.catalog.display', moduleId: 'estimating',
        title: 'Estimating catalog display',
        helpText: 'Controls pricebook/template page size, density, card size, inactive records, and source provenance.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            pageSize: 50, density: 'comfortable', cardSize: 'standard',
            showInactivePricebookItems: false, showExternalProvenance: true,
            showArchivedTemplates: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: estimatingCatalogDisplayValueSchema,
});
export const estimatingCatalogCopyValueSchema = z.strictObject({
    pricebookNavigationLabel: label,
    pricebookTitle: label,
    pricebookDescription: z.string().trim().min(1).max(300),
    templatesNavigationLabel: label,
    templatesTitle: label,
    templatesDescription: z.string().trim().min(1).max(300),
    searchLabel: label,
    categoryLabel: label,
    itemNameLabel: label,
    unitLabel: label,
    unitPriceLabel: label,
    skuLabel: label,
    sourceLabel: label,
    addCategoryAction: label,
    addItemAction: label,
    reviseAction: label,
    archiveAction: label,
    restoreAction: label,
    importAction: label,
    exportAction: label,
    addTemplateAction: label,
    applyTemplateAction: label,
    duplicateEstimateAction: label,
    generatePackageAction: label,
    loadingLabel: label,
    savedLabel: label,
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    emptyPricebook: z.string().trim().min(1).max(300),
    emptyTemplates: z.string().trim().min(1).max(300),
});
export const estimatingCatalogCopySetting = definePlatformSetting({
    definition: {
        key: 'estimating.copy.catalog', moduleId: 'estimating', title: 'Estimating catalog copy',
        helpText: 'Controls tenant-editable pricebook, template, duplicate, and package labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            pricebookNavigationLabel: 'Pricebook', pricebookTitle: 'Estimating pricebook',
            pricebookDescription: 'Manage retained categories, exact prices, source provenance, and deterministic imports.',
            templatesNavigationLabel: 'Estimate templates', templatesTitle: 'Estimate templates',
            templatesDescription: 'Build reusable estimating content with current-rate refresh and exact smart quantities.',
            searchLabel: 'Search', categoryLabel: 'Category', itemNameLabel: 'Item name',
            unitLabel: 'Unit', unitPriceLabel: 'Unit price', skuLabel: 'SKU', sourceLabel: 'Source',
            addCategoryAction: 'Add category', addItemAction: 'Add pricebook item',
            reviseAction: 'Save revision', archiveAction: 'Archive', restoreAction: 'Restore',
            importAction: 'Import CSV', exportAction: 'Export CSV', addTemplateAction: 'Add template',
            applyTemplateAction: 'Apply template', duplicateEstimateAction: 'Duplicate estimate',
            generatePackageAction: 'Generate Standard, Premium, and Complete',
            loadingLabel: 'Loading estimating catalog…', savedLabel: 'Saved',
            loadError: 'The estimating catalog could not be loaded.',
            mutationError: 'The estimating catalog change could not be saved. Refresh and try again.',
            emptyPricebook: 'No pricebook items match this view.',
            emptyTemplates: 'No estimate templates match this view.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: estimatingCatalogCopyValueSchema,
});
export const estimatingXactimateConfigurationValueSchema = z.strictObject({
    maximumImportBytes: z.union([
        z.literal(1_000_000), z.literal(2_500_000), z.literal(5_000_000),
    ]),
    maximumNormalizedLines: z.number().int().min(1).max(25_000),
    maximumActiveExportJobs: z.number().int().min(1).max(1_000),
    defaultAdapterKey: z.enum(['xactimate-cloud', 'xactimate-windows']),
    maximumCompileAttempts: z.number().int().min(1).max(10),
    workerOfflineSeconds: z.number().int().min(30).max(3_600),
});
export const estimatingXactimateConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'estimating.xactimate.configuration', moduleId: 'estimating',
        title: 'Xactimate workflow behavior',
        helpText: 'Controls bounded input normalization, retained export-job capacity, adapter selection, retries, and offline status. Provider execution remains external.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            maximumImportBytes: 2_500_000,
            maximumNormalizedLines: 10_000,
            maximumActiveExportJobs: 100,
            defaultAdapterKey: 'xactimate-cloud',
            maximumCompileAttempts: 3,
            workerOfflineSeconds: 60,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'protected',
    },
    valueSchema: estimatingXactimateConfigurationValueSchema,
});
export const estimatingXactimateDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showSpecHash: z.boolean(),
    showValidationWarnings: z.boolean(),
    showAdapterDetails: z.boolean(),
});
export const estimatingXactimateDisplaySetting = definePlatformSetting({
    definition: {
        key: 'estimating.xactimate.display', moduleId: 'estimating',
        title: 'Xactimate workflow display',
        helpText: 'Controls Xactimate job density, card sizing, provenance, validation, and adapter details.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            pageSize: 50, density: 'comfortable', cardSize: 'standard',
            showSpecHash: true, showValidationWarnings: true, showAdapterDetails: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: estimatingXactimateDisplayValueSchema,
});
export const estimatingXactimateCopyValueSchema = z.strictObject({
    navigationLabel: label,
    title: label,
    description: z.string().trim().min(1).max(300),
    providerBoundaryTitle: label,
    normalizeTitle: label,
    normalizeAction: label,
    sourceTextLabel: label,
    sourceFileNameLabel: label,
    formatLabel: label,
    autoFormatLabel: label,
    csvFormatLabel: label,
    pasteFormatLabel: label,
    projectIdLabel: label,
    sketchSpecLabel: label,
    adapterLabel: label,
    cloudAdapterLabel: label,
    windowsAdapterLabel: label,
    reasonLabel: label,
    validateAction: label,
    createJobAction: label,
    retryAction: label,
    jobsTitle: label,
    statusFilterLabel: label,
    allStatusesLabel: label,
    loadingLabel: label,
    normalizedLinesLabel: label,
    validationValidLabel: label,
    validationBlockedLabel: label,
    validationErrorsLabel: label,
    roomsLabel: label,
    wallsLabel: label,
    openingsLabel: label,
    revisionLabel: label,
    attemptsLabel: label,
    specificationHashLabel: label,
    compilerLabel: label,
    compiledOutputLabel: label,
    bytesLabel: label,
    lastErrorLabel: label,
    jobCreatedLabel: label,
    jobDeduplicatedLabel: label,
    retryQueuedLabel: label,
    noJobs: z.string().trim().min(1).max(300),
    providerBoundaryNotice: z.string().trim().min(1).max(500),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    permissionDenied: z.string().trim().min(1).max(240),
    statusLabels: z.strictObject({
        validation_failed: label,
        ready_for_compile: label,
        compiling: label,
        compiled: label,
        compile_failed: label,
        import_verified: label,
        cancelled: label,
    }),
});
export const estimatingXactimateCopySetting = definePlatformSetting({
    definition: {
        key: 'estimating.copy.xactimate', moduleId: 'estimating', title: 'Xactimate workflow copy',
        helpText: 'Controls tenant-editable Xactimate normalization, validation, queue, retry, and provider-boundary labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Xactimate', title: 'Xactimate workspace',
            description: 'Normalize estimate input, validate retained sketch specifications, and manage traceable ESX export jobs.',
            providerBoundaryTitle: 'Provider boundary',
            normalizeTitle: 'Normalize Xactimate input', normalizeAction: 'Normalize input',
            sourceTextLabel: 'Xactimate text or CSV', sourceFileNameLabel: 'Source filename',
            formatLabel: 'Input format', autoFormatLabel: 'Auto detect',
            csvFormatLabel: 'CSV', pasteFormatLabel: 'Pasted lines',
            projectIdLabel: 'Project ID', sketchSpecLabel: 'Validated sketch specification',
            adapterLabel: 'Adapter boundary', cloudAdapterLabel: 'Xactimate cloud adapter',
            windowsAdapterLabel: 'Xactimate Windows adapter', reasonLabel: 'Change reason',
            validateAction: 'Validate specification', createJobAction: 'Create export job',
            retryAction: 'Retry export', jobsTitle: 'Export jobs', statusFilterLabel: 'Status',
            allStatusesLabel: 'All statuses', loadingLabel: 'Loading Xactimate jobs…',
            normalizedLinesLabel: 'normalized line(s) retained',
            validationValidLabel: 'Valid specification', validationBlockedLabel: 'Specification blocked',
            validationErrorsLabel: 'blocking validation error(s)', roomsLabel: 'rooms',
            wallsLabel: 'walls', openingsLabel: 'openings', revisionLabel: 'Revision',
            attemptsLabel: 'Attempts', specificationHashLabel: 'Specification hash',
            compilerLabel: 'Compiler', compiledOutputLabel: 'Compiled output',
            bytesLabel: 'bytes',
            lastErrorLabel: 'Last error', jobCreatedLabel: 'Export job created',
            jobDeduplicatedLabel: 'Existing active job retained', retryQueuedLabel: 'Retry queued',
            noJobs: 'No Xactimate export jobs match this view.',
            providerBoundaryNotice: 'CREST retains job intent and results. External Xactimate tooling runs only through an authorized adapter.',
            loadError: 'The Xactimate workspace could not be loaded.',
            mutationError: 'The Xactimate workflow change could not be saved. Refresh and try again.',
            permissionDenied: 'You do not have permission to change this Xactimate workflow.',
            statusLabels: {
                validation_failed: 'Validation failed', ready_for_compile: 'Ready for compile',
                compiling: 'Compiling', compiled: 'Compiled', compile_failed: 'Compile failed',
                import_verified: 'Import verified', cancelled: 'Cancelled',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: estimatingXactimateCopyValueSchema,
});
export const estimatingPortalProposalsConfigurationValueSchema = z.strictObject({
    allowCustomerSelection: z.boolean(),
    requireSignedDocument: z.boolean(),
});
const estimatingPortalProposalConfigurationV2Additions = {
    requireSignedDocument: false,
};
export const estimatingPortalProposalsConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'estimating.portal-proposals.configuration', moduleId: 'estimating',
        title: 'Customer Portal proposal behavior',
        helpText: 'Controls whether a scoped customer link may lock an exact released proposal option.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 2,
        codedFallback: { allowCustomerSelection: true, requireSignedDocument: false },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'protected',
    },
    valueSchema: estimatingPortalProposalsConfigurationValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...estimatingPortalProposalConfigurationV2Additions,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        }],
});
export const estimatingPortalProposalsDisplayValueSchema = z.strictObject({
    enabled: z.boolean(),
    showLineItems: z.boolean(),
    showSubtotal: z.boolean(),
    showOptionDescriptions: z.boolean(),
});
export const estimatingPortalProposalsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'estimating.portal-proposals.display', moduleId: 'estimating',
        title: 'Customer Portal proposal display',
        helpText: 'Controls the scoped proposal panel and customer-safe option detail.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            enabled: true, showLineItems: true, showSubtotal: true, showOptionDescriptions: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: estimatingPortalProposalsDisplayValueSchema,
});
export const estimatingPortalProposalsCopyValueSchema = z.strictObject({
    title: label,
    description: z.string().trim().min(1).max(300),
    selectAction: label,
    declineAction: label,
    selectedLabel: label,
    acceptedByLabel: label,
    consentNotice: z.string().trim().min(1).max(500),
    signatureRequiredLabel: z.string().trim().min(1).max(240),
    signedDocumentReadyLabel: z.string().trim().min(1).max(240),
    subtotalLabel: label,
    taxLabel: label,
    totalLabel: label,
    unavailableLabel: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
});
const estimatingPortalProposalCopyV2Additions = {
    declineAction: 'Decline this option',
    signatureRequiredLabel: 'Sign the matching proposal document below before selecting this option.',
    signedDocumentReadyLabel: 'The matching signed proposal document is ready.',
};
export const estimatingPortalProposalsCopySetting = definePlatformSetting({
    definition: {
        key: 'estimating.copy.portal-proposals', moduleId: 'estimating',
        title: 'Customer Portal proposal copy',
        helpText: 'Controls tenant-editable public proposal labels without changing retained money or acceptance meaning.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 2,
        codedFallback: {
            title: 'Your proposal options',
            description: 'Review the exact options prepared for your project.',
            selectAction: 'Select this option', selectedLabel: 'Selected option',
            declineAction: 'Decline this option',
            acceptedByLabel: 'Your full name',
            consentNotice: 'Selecting an option records your name, the exact proposal version, and the acceptance time.',
            signatureRequiredLabel: 'Sign the matching proposal document below before selecting this option.',
            signedDocumentReadyLabel: 'The matching signed proposal document is ready.',
            subtotalLabel: 'Subtotal', taxLabel: 'Tax', totalLabel: 'Total',
            unavailableLabel: 'Proposal options are not currently available.',
            mutationError: 'The proposal changed before your selection was saved. Refresh and review it again.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: estimatingPortalProposalsCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => ({
                ...estimatingPortalProposalCopyV2Additions,
                ...(typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}),
            }),
        }],
});
export const estimatingSettingRegistrations = [
    estimatingEstimatesConfigurationSetting,
    estimatingEstimatesDisplaySetting,
    estimatingEstimatesCopySetting,
    estimatingProposalsDisplaySetting,
    estimatingProposalsCopySetting,
    estimatingCatalogConfigurationSetting,
    estimatingCatalogDisplaySetting,
    estimatingCatalogCopySetting,
    estimatingXactimateConfigurationSetting,
    estimatingXactimateDisplaySetting,
    estimatingXactimateCopySetting,
    estimatingPortalProposalsConfigurationSetting,
    estimatingPortalProposalsDisplaySetting,
    estimatingPortalProposalsCopySetting,
];
//# sourceMappingURL=estimating-setting-definitions.js.map